import React, { useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { isValidString } from '../../../common/CommonUtilsMethods';
import { CreateTrustVaultDocumentSubTypeAgencyUserAPICall } from '../api/TrustVaultPhiDocumentsAgencyUserAPI';
import {
    CreateTrustVaultDocumentSubTypeReqDTO,
    TrustVaultDocumentSubTypeRespDTO,
} from '../interfaces/TrustVaultPhiDocumentsAgencyUserInterfaces';

type CreateDocumentSubTypeApiCall = (
    request: CreateTrustVaultDocumentSubTypeReqDTO
) => Promise<TrustVaultDocumentSubTypeRespDTO | null>;

interface DefineTrustVaultDocumentSubTypeModalProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly onSubTypeCreated: (createdSubType: TrustVaultDocumentSubTypeRespDTO) => void;
    readonly createDocumentSubTypeApiCall?: CreateDocumentSubTypeApiCall;
    readonly helperText?: string;
}

const DefineTrustVaultDocumentSubTypeModal = ({
    open,
    onClose,
    onSubTypeCreated,
    createDocumentSubTypeApiCall = CreateTrustVaultDocumentSubTypeAgencyUserAPICall,
    helperText = 'The label shown when tagging worker documents',
}: DefineTrustVaultDocumentSubTypeModalProps): React.ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const [subTypeLabel, setSubTypeLabel] = useState<string>('');
    const [subTypeDescription, setSubTypeDescription] = useState<string>('');
    const [requiresExpiryDate, setRequiresExpiryDate] = useState<boolean>(false);
    const [isCreating, setIsCreating] = useState<boolean>(false);

    const handleClose = (): void => {
        if (isCreating) {
            return;
        }
        setSubTypeLabel('');
        setSubTypeDescription('');
        setRequiresExpiryDate(false);
        onClose();
    };

    const handleCreateClick = async (): Promise<void> => {
        const trimmedLabel: string = subTypeLabel.trim();
        if (!isValidString(trimmedLabel)) {
            enqueueSnackbar('Document sub-type label is required', { variant: 'error' });
            return;
        }

        const trimmedDescription: string = subTypeDescription.trim();

        setIsCreating(true);
        try {
            const createdSubType = await createDocumentSubTypeApiCall({
                subTypeLabel: trimmedLabel,
                requiresExpiryDate,
                ...(isValidString(trimmedDescription) ? { description: trimmedDescription } : {}),
            });
            if (createdSubType === null) {
                enqueueSnackbar('Unable to create document sub-type', { variant: 'error' });
                return;
            }
            enqueueSnackbar('Document sub-type created', { variant: 'success' });
            setSubTypeLabel('');
            setSubTypeDescription('');
            setRequiresExpiryDate(false);
            onSubTypeCreated(createdSubType);
            onClose();
        } catch (error) {
            console.error('Failed to create TrustVault document sub-type:', error);
            enqueueSnackbar('Unable to create document sub-type', { variant: 'error' });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>Define new document sub-type</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Sub-type label"
                    size="small"
                    fullWidth
                    value={subTypeLabel}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        setSubTypeLabel(event.target.value);
                    }}
                    disabled={isCreating}
                    helperText="A reusable label for this kind of worker document (e.g. WHMIS renewal)"
                />
                <TextField
                    margin="dense"
                    label="Description (optional)"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={subTypeDescription}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        setSubTypeDescription(event.target.value);
                    }}
                    disabled={isCreating}
                    helperText="Optional notes for your agency team about when to use this sub-type"
                />
                <FormControlLabel
                    sx={{ mt: 1, alignItems: 'flex-start' }}
                    control={
                        <Checkbox
                            checked={requiresExpiryDate}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                                setRequiresExpiryDate(event.target.checked);
                            }}
                            disabled={isCreating}
                            size="small"
                        />
                    }
                    label={
                        <div>
                            <div style={{ fontSize: '14px', fontWeight: 500 }}>Require expiry date</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                Uploads tagged with this type must include the document&apos;s expiry date
                            </div>
                        </div>
                    }
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isCreating}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        void handleCreateClick();
                    }}
                    disabled={isCreating || !isValidString(subTypeLabel.trim())}
                >
                    {isCreating ? <CircularProgress size={18} /> : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { DefineTrustVaultDocumentSubTypeModal };
